// ----------------------------------------------------------------------------
// ChordUploadItem — one row in a Song Studio list: status tile, title with
// inline rename (Enter or blur saves, Esc cancels), detected key / BPM /
// chords / stems, purge notice, status pill and remove.
// ----------------------------------------------------------------------------

import { useRef, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import type { ChordUpload, ChordUploadStatus } from '@app/api/chords';
import { IndeterminateBar, StatusPill, type StatusTone } from '@app/components/ui';
import { relativeWhen } from '@app/lib/format';

const STATUS_META: Record<ChordUploadStatus, { label: string; tone: StatusTone }> = {
  pending: { label: 'Queued', tone: 'amb' },
  processing: { label: 'Analyzing', tone: 'info' },
  completed: { label: 'Ready', tone: 'ok' },
  failed: { label: 'Failed', tone: 'acc' },
};

interface ChordUploadItemProps {
  upload: ChordUpload;
  onRemove: (sourceId: string) => void;
  onRename: (sourceId: string, title: string) => void;
}

export const ChordUploadItem: React.FC<ChordUploadItemProps> = ({ upload, onRemove, onRename }) => {
  const status = STATUS_META[upload.status] ?? STATUS_META.pending;
  const inProgress = upload.status === 'pending' || upload.status === 'processing';
  const failed = upload.status === 'failed';

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(upload.title);
  // Guards against committing twice (e.g. an Enter keypress plus the resulting
  // blur), which would fire a redundant rename request.
  const committedRef = useRef(false);

  const startEdit = () => {
    committedRef.current = false;
    setDraft(upload.title);
    setEditing(true);
  };
  const cancel = () => {
    committedRef.current = true;
    setEditing(false);
    setDraft(upload.title);
  };
  const save = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    setEditing(false);
    const next = draft.trim();
    if (next && next !== upload.title) {
      onRename(upload.sourceId, next);
    } else {
      setDraft(upload.title);
    }
  };

  const meta = [
    upload.detectedKey ? `Key ${upload.detectedKey}` : null,
    upload.bpm ? `${Math.round(upload.bpm)} BPM` : null,
    upload.chords?.length ? `${upload.chords.length} chords` : null,
    upload.includeStems && upload.status === 'completed' ? 'stems' : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const by = [upload.artist, upload.createdAt ? relativeWhen(upload.createdAt) : null].filter(Boolean).join(', ');

  const subtitle = failed
    ? upload.error || 'Analysis failed'
    : upload.status === 'completed'
      ? [meta || 'Analyzed', by].filter(Boolean).join(' · ')
      : [upload.status === 'pending' ? 'Waiting for analysis' : 'Detecting chords and key', by].filter(Boolean).join(' · ');

  // Retention grace window: a downgrade locked this record instead of deleting
  // it. It stays viewable until the purge date; re-subscribing restores it.
  const purgeLabel = upload.purgeAt
    ? new Date(upload.purgeAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : null;

  return (
    <div className="flex items-center gap-3.5 px-[18px] py-3.5 border-b border-line last:border-b-0">
      {inProgress ? (
        <span className="w-[42px] h-[42px] rounded-[12px] bg-fill2 flex items-center justify-center shrink-0" aria-hidden="true">
          <span className="wp-spinner w-4 h-4" style={{ animationDuration: '.8s' }} />
        </span>
      ) : (
        <span
          aria-hidden="true"
          className={`w-[42px] h-[42px] rounded-[12px] font-mono font-bold text-[14px] flex items-center justify-center shrink-0 ${failed ? 'bg-acc-bg text-acc' : 'bg-fill2 text-t1'}`}
        >
          {failed ? '!' : upload.detectedKey || '–'}
        </span>
      )}

      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex gap-1.5 items-center">
            <input
              autoFocus
              aria-label="Song title"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={save}
              onKeyDown={(e) => {
                if (e.key === 'Enter') save();
                if (e.key === 'Escape') cancel();
              }}
              className="wp-field flex-1 min-w-0 h-9 px-3 !rounded-[10px] text-[14.5px] font-semibold"
            />
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={save}
              aria-label="Save title"
              className="w-9 h-9 rounded-[10px] bg-t1 text-bg text-[15px] shrink-0"
            >
              ✓
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={cancel}
              aria-label="Cancel rename"
              className="w-9 h-9 rounded-[10px] border border-line2 bg-fill text-t2 text-[15px] shrink-0"
            >
              ×
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-[14.5px] font-semibold truncate">{upload.title}</span>
            <button
              type="button"
              onClick={startEdit}
              aria-label={`Rename ${upload.title}`}
              title="Rename"
              className="w-[26px] h-[26px] rounded-[8px] text-t4 hover:bg-fill2 hover:text-t1 flex items-center justify-center shrink-0"
            >
              <Pencil size={14} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
        )}
        <div className={`text-[12.5px] mt-0.5 truncate ${failed ? 'text-acc' : 'text-t4'}`}>{subtitle}</div>
        {purgeLabel && <div className="text-[12px] text-amb mt-[3px]">Scheduled for deletion on {purgeLabel} · re-subscribe to restore</div>}
        {upload.status === 'processing' && <IndeterminateBar className="mt-2 max-w-[220px]" />}
      </div>

      <StatusPill tone={status.tone}>{status.label}</StatusPill>
      <button
        type="button"
        onClick={() => onRemove(upload.sourceId)}
        aria-label={`Remove ${upload.title}`}
        className="w-8 h-8 rounded-full text-t4 hover:bg-acc-bg hover:text-acc flex items-center justify-center shrink-0"
      >
        <Trash2 size={15} strokeWidth={2} aria-hidden="true" />
      </button>
    </div>
  );
};
